import { useMemo } from 'react'
import { Calendar, Clock, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  CardTypeBadge,
  HtmlContent,
  ImageSlider,
  formatCarteCapacity,
  formatHourRange,
  formatPeriode,
  formatTND,
  StockAccessInfo,
} from '../shared'

const CarteDetailsStep = ({ carte }) => {
  const { t } = useTranslation()
  const activeImages = useMemo(
    () => (carte?.images ?? []).filter((img) => img.active && img.url),
    [carte?.images],
  )

  const perPax = carte?.pricing_type === 'per_pax'

  if (!carte) return null

  return (
    <div className="flex min-h-[calc(92vh-11rem)] flex-col gap-3">
      <ImageSlider images={activeImages} alt={carte.label} className="aspect-video max-h-72 w-full shrink-0" />

      <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/40 px-4 py-3 sm:flex-row sm:items-stretch sm:justify-between sm:gap-6">
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
          <h3 className="text-xl font-semibold leading-snug text-gray-900">{carte.label}</h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
              {formatPeriode(carte.start_date, carte.end_date)}
            </span>
            {(carte.start_hour || carte.end_hour) && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4 shrink-0 text-gray-400" />
                {formatHourRange(carte.start_hour, carte.end_hour)}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-4 w-4 shrink-0 text-gray-400" />
              {formatCarteCapacity(carte, t)}
            </span>
          </div>
          <StockAccessInfo carte={carte} appearance="banner" />
        </div>

        <div className="flex shrink-0 flex-col items-start justify-between gap-3 sm:items-end sm:text-right">
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <CardTypeBadge type={carte.type} />
            <span className="text-sm text-gray-500">{carte.facility_label}</span>
          </div>
          <div className="flex flex-col items-start gap-0.5 sm:items-end">
            <div className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-2xl font-bold text-brand-700">{formatTND(carte.price)}</span>
              {perPax && <span className="text-sm text-gray-400">{t('cartesAcces.recherche.details.perPerson')}</span>}
            </div>
            {carte.price_consumption > 0 && (
              <span className="text-xs text-gray-400">
                {t('cartesAcces.recherche.details.consumable', {
                  amount: formatTND(carte.price_consumption),
                })}
              </span>
            )}
          </div>
        </div>
      </div>

      {carte.details && (
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/60 p-4">
          <HtmlContent html={carte.details} />
        </div>
      )}
    </div>
  )
}

export default CarteDetailsStep
